import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { IcoCheck } from '@components/icons';
import { rpx } from 'client/lib/rpx-client';
import { useIntl } from 'shared/intl/use-intl';
import type { Checkin } from 'server/dbx/checkins';
import { TodaysCheckinCard } from './todays-checkin-card';
import { swallowCheckinLoadFailure } from './load-failure';
import { isStandupLesson, weekStandupDays } from './week-standups';
import {
  loadTrackerGlance,
  TRACKER_LINK_CLASS,
  TrackerGlance,
} from '@course/components/tracker/glance';

type MyCheckins = Awaited<ReturnType<typeof rpx.checkins.getMyCheckins>>;

/** This week's standup days when the bar shows on this lesson, else nothing. */
function barWeekDays(data: MyCheckins, moduleDate?: string) {
  const { settings, localDate } = data;
  if (
    !settings.checkinsEnabled ||
    !isStandupLesson({ moduleDate, startDate: settings.startDate, endDate: settings.endDate }) ||
    (settings.startDate && localDate < settings.startDate) ||
    (settings.endDate && localDate > settings.endDate)
  ) {
    return;
  }
  const weekDays = weekStandupDays({
    localDate,
    days: settings.days,
    startDate: settings.startDate,
    endDate: settings.endDate,
  });
  return weekDays.length ? weekDays : undefined;
}

/**
 * Today's standup and the week's count, above a lesson. Most students reach
 * lessons straight from an email and never pass the course home page, which
 * is the only other place the standup is offered. Shown only on lessons from
 * the standup weeks.
 */
export function LessonStandupBar({
  courseId,
  moduleDate,
  trackerHref,
}: {
  courseId: UUID;
  moduleDate?: string;
  /** The Tracker tab, when this student's tracker numbers should show. */
  trackerHref?: string;
}) {
  const intl = useIntl();
  const [data, setData] = useState<MyCheckins>();
  const [tracker, setTracker] = useState<TrackerGlance>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    rpx.checkins
      .getMyCheckins({ courseId, historyLimit: 7 })
      .then((result) => {
        if (!isCurrent) {
          return;
        }
        setData(result);
        if (trackerHref && barWeekDays(result, moduleDate)) {
          loadTrackerGlance(courseId).then((glance) => {
            if (isCurrent) {
              setTracker(glance);
            }
          });
        }
      })
      .catch(swallowCheckinLoadFailure);
    return () => {
      isCurrent = false;
    };
  }, [courseId, moduleDate, trackerHref]);

  const weekDays = data && barWeekDays(data, moduleDate);
  if (!data || !weekDays) {
    return;
  }
  const { settings, localDate, isActiveDay, today } = data;
  // Autosave creates a row as soon as someone starts typing, so a draft is not
  // a posted standup: only submitted ones count toward the week or "done".
  const posted = new Set(data.checkins.filter((c) => !c.isDraft).map((c) => c.checkinDate));
  if (today && !today.isDraft) {
    posted.add(today.checkinDate);
  }
  const count = weekDays.filter((day) => posted.has(day)).length;
  const isDone = !!today && !today.isDraft;
  const isDue = isActiveDay && !isDone;

  // Runs on every autosave too, so it must not close the form (that left
  // students clicking Edit after every pause in typing).
  function onSaved(checkin: Checkin) {
    setData(
      (d) =>
        d && {
          ...d,
          today: checkin,
          checkins: [checkin, ...d.checkins.filter((c) => c.checkinDate !== checkin.checkinDate)],
        },
    );
  }

  return (
    <section class="flex flex-col gap-3 mb-6 font-sans">
      <div
        class={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
          isDone
            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
            : isDue
              ? 'border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950'
              : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900'
        }`}
      >
        <div class="flex flex-col gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <strong class="text-gray-900 dark:text-gray-100">
              {intl('This week: {count:number} of {total:number} standups', {
                count,
                total: weekDays.length,
              })}
            </strong>
            <span class="flex gap-1">
              {weekDays.map((day) => (
                <span
                  key={day}
                  class={`inline-flex items-center gap-0.5 text-xs rounded px-1.5 py-0.5 border ${
                    posted.has(day)
                      ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900 dark:border-green-700 dark:text-green-200'
                      : day === localDate
                        ? 'bg-white border-indigo-500 text-indigo-800 font-semibold dark:bg-gray-800 dark:text-indigo-200'
                        : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                  }`}
                >
                  {posted.has(day) && <IcoCheck class="size-3" />}
                  {dayjs(day).format('ddd')}
                </span>
              ))}
            </span>
          </div>
          {isActiveDay && (
            <span class="text-sm text-gray-600 dark:text-gray-300">
              {isDone
                ? intl("Today's standup is done")
                : intl("Today's standup is not posted yet · About 2 minutes · Your coach reads it")}
            </span>
          )}
          {tracker && trackerHref && (
            <span class="flex flex-wrap items-center gap-x-3 text-sm text-gray-700 dark:text-gray-300">
              <span>{intl('This week: {line:string}', { line: tracker.weekBrief })}</span>
              <a class={`${TRACKER_LINK_CLASS} -my-3`} href={trackerHref}>
                {intl('Open tracker')}
              </a>
            </span>
          )}
        </div>
        {isActiveDay &&
          (isDone ? (
            <BtnSecondary onClick={() => setIsOpen((x) => !x)}>
              {isOpen ? intl('Close') : intl('Edit')}
            </BtnSecondary>
          ) : (
            <BtnPrimary onClick={() => setIsOpen((x) => !x)}>
              {isOpen ? intl('Close') : intl('Post standup')}
            </BtnPrimary>
          ))}
      </div>
      {isOpen && isActiveDay && (
        <TodaysCheckinCard
          courseId={courseId}
          settings={settings}
          localDate={localDate}
          isActiveDay={isActiveDay}
          today={today}
          onSaved={onSaved}
          onSubmitted={() => setIsOpen(false)}
          compact
          tracker={tracker}
          trackerHref={trackerHref}
        />
      )}
    </section>
  );
}
